import { computed, ref, watch, getCurrentScope, onScopeDispose, type Ref } from 'vue'
import { activeInventory, activityKey, activityHasEnded, resolveFulfillment, type InventoryStatus } from './purchase'

interface Options {
  listing: Ref<ProductV3.ListingDetailV3>
  sku: Ref<ProductV3.Sku | undefined>
  skuId: Ref<number>
  activity: Ref<ProductV3.SelectedSkuActivity | undefined>
  quantity: Ref<number>
  address: Ref<Address.IAddressItem | undefined>
  delivery: Ref<ProductV3.ShippingTime | undefined>
  fetchInventory: (skuId: number) => Promise<{ inventory: ProductV3.Inventory, activity?: ProductV3.SelectedSkuActivity | null }>
  fetchShipping: (params: any) => Promise<ProductV3.ShippingTime | null>
}

/** All versions and expired identities belong to one page instance, including SSR. */
export const usePurchaseRequests = (options: Options) => {
  const inventoryStatus = ref<InventoryStatus>('ready')
  const deliveryStatus = ref<'idle' | 'loading' | 'ready' | 'error'>('idle')
  let selection = 0, stockRequest = 0, shippingRequest = 0, stockRevision = 0, disposed = false
  const expired = new Set<string>()
  const fulfillment = computed(() => inventoryStatus.value === 'ready'
    ? resolveFulfillment(activeInventory(options.sku.value, options.activity.value), options.quantity.value) : undefined)
  const capture = () => ({ skuId: options.skuId.value, selection })
  const isCurrent = (token: ReturnType<typeof capture>) => !disposed && token.selection === selection && token.skuId === options.skuId.value
  const context = () => JSON.stringify([
    selection, stockRevision, options.listing.value.listingId, options.skuId.value, options.quantity.value,
    options.address.value?.id, options.address.value?.areaNameList?.slice(-1)[0]?.id,
    activityKey(options.activity.value), fulfillment.value?.warehouseId, fulfillment.value?.logisticsType
  ])
  let invalidatedDeliveryContext = context()
  const emptyDelivery = (): ProductV3.ShippingTime | undefined => fulfillment.value ? {
    listingId: options.listing.value.listingId, skuId: options.skuId.value,
    logisticsType: fulfillment.value.logisticsType, shippingTimeDescription: '', agingStart: null, agingEnd: null
  } : undefined
  const invalidateDelivery = () => {
    invalidatedDeliveryContext = context()
    shippingRequest++
    deliveryStatus.value = 'idle'
    options.delivery.value = emptyDelivery()
  }
  const stopSelection = watch(options.skuId, () => {
    selection++
    stockRequest++
    inventoryStatus.value = 'loading'
    options.activity.value = undefined
    invalidateDelivery()
  }, { flush: 'sync' })
  const stopContext = watch(context, value => {
    // A synchronous page watcher may already have refreshed this exact context.
    if (value !== invalidatedDeliveryContext) invalidateDelivery()
  }, { flush: 'sync' })

  const refreshInventory = async (): Promise<boolean> => {
    const token = capture(), request = ++stockRequest
    inventoryStatus.value = 'loading'
    options.activity.value = undefined
    invalidateDelivery()
    if (!token.skuId || !options.sku.value) return false
    try {
      const result = await options.fetchInventory(token.skuId)
      if (!isCurrent(token) || request !== stockRequest) return false
      if (!result?.inventory || result.activity && result.activity.skuId !== token.skuId) throw new Error('Inventory context mismatch')
      const activity = result.activity || undefined
      if (activity && (expired.has(activityKey(activity)) || activityHasEnded(activity))) {
        expired.add(activityKey(activity))
        throw new Error('Activity has already ended')
      }
      Object.assign(options.sku.value!.inventory, result.inventory)
      options.activity.value = activity
      stockRevision++
      inventoryStatus.value = 'ready'
      return true
    } catch {
      if (isCurrent(token) && request === stockRequest) inventoryStatus.value = 'error'
      return false
    }
  }
  const refreshDelivery = async (): Promise<void> => {
    invalidateDelivery()
    if (disposed || inventoryStatus.value !== 'ready' || !options.sku.value || !fulfillment.value) return
    const request = shippingRequest, identity = context(), token = capture()
    const params: any = {
      listingId: options.listing.value.listingId, skuId: token.skuId, skuCount: options.quantity.value,
      storeId: options.listing.value.storeId, shippingType: options.listing.value.shippingType,
      warehouseId: fulfillment.value.warehouseId, logisticsType: fulfillment.value.logisticsType
    }
    const area = options.address.value?.areaNameList?.slice(-1)[0]
    if (area) params.deliveryAreaId = area.id
    const current = () => isCurrent(token) && request === shippingRequest && identity === context()
    deliveryStatus.value = 'loading'
    try {
      const response = await options.fetchShipping(params)
      if (!current()) return
      if (!response || response.skuId !== token.skuId || response.listingId !== params.listingId) throw new Error('Shipping context mismatch')
      options.delivery.value = {
        ...emptyDelivery()!,
        shippingTimeDescription: response.shippingTimeDescription || '',
        agingStart: response.agingStart ?? null, agingEnd: response.agingEnd ?? null
      }
      deliveryStatus.value = 'ready'
    } catch {
      if (current()) deliveryStatus.value = 'error'
    }
  }
  const expireActivity = (activity?: ProductV3.SelectedSkuActivity): boolean => {
    const key = activityKey(activity)
    if (disposed || !key || activity?.skuId !== options.skuId.value || key !== activityKey(options.activity.value) || expired.has(key)) return false
    expired.add(key)
    stockRequest++
    inventoryStatus.value = 'loading'
    options.activity.value = undefined
    invalidateDelivery()
    return true
  }
  const dispose = () => { disposed = true; selection++; stockRequest++; shippingRequest++; stopSelection(); stopContext() }
  if (getCurrentScope()) onScopeDispose(dispose)
  // Reuse the main response only when its data belongs to the selected SKU.
  if (options.activity.value && (options.activity.value.skuId !== options.skuId.value || activityHasEnded(options.activity.value))) {
    options.activity.value = undefined
    inventoryStatus.value = 'error'
    invalidateDelivery()
  } else if (options.delivery.value?.skuId !== options.skuId.value) {
    options.delivery.value = emptyDelivery()
  } else if (options.delivery.value && fulfillment.value) {
    options.delivery.value = { ...options.delivery.value, logisticsType: fulfillment.value.logisticsType }
  }
  return { inventoryStatus, deliveryStatus, fulfillment, capture, isCurrent, invalidateDelivery, refreshInventory, refreshDelivery, expireActivity, dispose }
}
